import { Band, DotItem, Eyebrow, Lead, SectionTitle } from '@/components/bedrock';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

/**
 * The control band — the trust argument, on a dark inset card.
 *
 * Its claims are the ones the product's threat model actually enforces
 * (`.claude/rules/security.md`: act-as-user identity, ADR-0153 confirmations,
 * the C34 audit trail, EU residency under ADR-0198), so the copy stays inside
 * what the platform can defend. "Never used to train anyone's model" is a
 * statement about the ZDR posture, not an aspiration.
 *
 * The four dots key to the brand ramp rather than to a semantic scale — they
 * are ordinal, not severity, so cyan → blue → violet → green reads as a
 * sequence and never as a status.
 */
export function Control(): ReactElement {
  const { t } = useT();

  return (
    <Band tone="band" id="control" space="lg" ambient aria-labelledby="control-title">
      <div
        data-bd-rise
        className="relative grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-start lg:gap-14"
      >
        <div>
          <Eyebrow onDark>{t('home.control.eyebrow')}</Eyebrow>
          <SectionTitle
            id="control-title"
            onDark
            className="mt-3 !text-[clamp(1.6rem,3vw,2.25rem)]"
          >
            {t('home.control.title')}
          </SectionTitle>
          <Lead onDark className="mt-3.5">
            {t('home.control.lead')}
          </Lead>
          <LocalizedLink
            to="/trust"
            // This link carried NO focus treatment, so it fell back to the
            // browser's default outline — which on a dark band is drawn in a
            // colour near-identical to its surroundings. Measured at 1.05:1
            // against both the control and the page behind it, i.e. an
            // indicator a keyboard user cannot see. Cyan on a dark ground, the
            // same convention `panel-classes.ts` and the how-it-works steps
            // take, and an outline rather than a ring because outline is a
            // separate property from box-shadow.
            className="mt-5 inline-block text-[0.9rem] font-medium text-[color:var(--bd-band-link)] underline-offset-4 hover:underline focus-visible:underline focus-visible:[outline:2px_solid_var(--bd-cyan)] focus-visible:[outline-offset:3px]"
          >
            {t('home.control.link')}{' '}
            <span aria-hidden="true" className="inline-block rtl:-scale-x-100">
              →
            </span>
          </LocalizedLink>
        </div>

        <ul className="flex flex-col gap-3.5">
          <DotItem onDark color="var(--bd-cyan)" title={t('home.control.item1.title')}>
            {t('home.control.item1.body')}
          </DotItem>
          <DotItem onDark color="var(--bd-blue)" title={t('home.control.item2.title')}>
            {t('home.control.item2.body')}
          </DotItem>
          <DotItem onDark color="var(--bd-violet)" title={t('home.control.item3.title')}>
            {t('home.control.item3.body')}
          </DotItem>
          <DotItem onDark color="var(--bd-ok)" title={t('home.control.item4.title')}>
            {t('home.control.item4.body')}
          </DotItem>
        </ul>
      </div>
    </Band>
  );
}
